import {
  replaceAllVerbatim,
  replaceVerbatim,
} from "@project/shared/src/replaceVerbatim";
import * as THREE from "three";
import { FOAM_OCCLUSION_ATTRIBUTE } from "../geometry/foamBlockGeometry";
import {
  noppenShade,
  noppenSlope,
  SPIKE_NUB_MM,
  SPIKE_PITCH_MM,
} from "../geometry/noppenSurface";
import {
  SLOT_DEPTH_MM,
  SLOT_PITCH_MM,
  SLOT_WIDTH_MM,
  slotFloorVisibility,
  slotShade,
} from "../geometry/slotSurface";
import { spikedPadNubHeight } from "../geometry/spikedPadGeometry";
import {
  heightMapToAoRoughnessReliefMap,
  heightMapToNormalMap,
  heightMapToReliefMap,
  slopeToNormalMap,
  valuesToGrayMap,
} from "./proceduralMaps";
import {
  type CellularLattice,
  cellularEdgeDistance,
  cellularLattice,
  tileableFbm,
} from "./proceduralNoise";

/**
 * Foam surfaces.
 *
 * Each pattern is baked once into a set of maps and cached: normals for the
 * shape of the pores, a roughness map because an open pore scatters more than
 * the skin around it, an ambient occlusion map because image-based lighting
 * arrives from every direction at once and cannot darken the inside of a pore
 * on its own, and relief for the parallax march. Those three scalar fields
 * share one RGB texture; the normal map remains separate.
 */
type FoamTextures = {
  normalMap: THREE.CanvasTexture;
  /** AO in R, roughness in G, relief in B; shared coordinates and filtering. */
  poreMap: THREE.CanvasTexture;
};

/** Foam is matte throughout; a pore mouth is duller still than the skin. */
const PORE_ROUGHNESS = 1;
const SKIN_ROUGHNESS = 0.82;

/**
 * Slope of the pore normal map, chosen for a mean texel tilt of 24 degrees.
 *
 * The pores are the only thing these maps carry now. The eggbox and the
 * grooves used to be blended into the same height field at 30% pore
 * amplitude; both come from their own macro maps since, baked from the same
 * surface definitions the geometry uses — see {@link getFoamMacroTextures}.
 */
const FOAM_NORMAL_INTENSITY = 0.7;

/**
 * 512, against the 256 this used to bake at.
 *
 * The old size was chosen because the bake is synchronous main-thread work at
 * first paint, and at the time the field cost 20 `Math.sin` calls per texel.
 * The cellular field below hashes with integers instead, so 512 x 512 now costs
 * about what 256 x 256 used to, and the extra resolution is not optional here:
 * see {@link FOAM_TILE_MM} for what a pore has to fit into.
 */
const TEXTURE_SIZE = 512;

/**
 * Pore diameter in millimetres.
 *
 * 0.2 mm is a fine grade — around 127 PPI, against the 10 PPI at 2.1 mm at the
 * coarse end of the range. At this size a pore is smaller than a screen pixel
 * for anything but a close-up, so what it contributes is a velvety grain and
 * the way that grain breaks up a highlight, rather than a visible cell.
 */
const FOAM_PORE_MM = 0.2;

/**
 * Voronoi cells across one tile.
 *
 * The number the texture can carry, not a physical quantity: at 512 pixels this
 * leaves five texels to a cell. More cells would put the struts below two
 * texels, where mipmapping erases them before the first level.
 */
const FOAM_CELLS_PER_TILE = 100;

/**
 * Edge length of one texture tile on the part, in millimetres.
 *
 * This used to be 300, which is where the foam went wrong. A pad tiles the
 * texture once per 300 mm, so at 256 pixels a texel covered 1.17 mm, while the
 * pores are 0.2 mm across. The pore structure was six texels finer than the
 * grid meant to carry it, so what the old height field actually painted was its
 * coarsest octave: 7.5 mm blobs. That is stucco.
 *
 * 20 mm at 512 pixels puts a texel at 0.039 mm and a pore at five of them,
 * which is a strut two texels wide — thin, but the strut network is the whole
 * pattern, so it is better spent there than on a wider tile. The tile is
 * derived, not chosen: the pore size is the physical constant, and the cell
 * count is what the texture can hold, so the tile is the product of the two.
 *
 * The cost is that the pattern wraps every 20 mm. Nothing in it is larger than
 * a pore, so there is no feature for the eye to lock onto and count, and at
 * ordinary viewing distance a pore is well under a pixel anyway.
 */
const FOAM_TILE_MM = FOAM_CELLS_PER_TILE * FOAM_PORE_MM;

/**
 * Ligament thickness, as a fraction of a cell.
 *
 * The struts are drawn where the nearest and second-nearest cell centres are
 * nearly equidistant, so this is the width of the band around a Voronoi edge
 * that counts as solid. Real foam is about 97% void by volume, but a cut face
 * shows the severed struts of the cells behind as well as the ones in the plane
 * of the cut, so the solid fraction that reads on the surface is much higher
 * than the porosity would suggest.
 *
 * A fraction of a cell rather than a length, so it has to rise as the cells get
 * smaller: at five texels to a cell, the 0.42 this ran at while the cells were
 * twice as wide would leave a strut of two texels with nothing either side of
 * it to shade against.
 */
const FOAM_STRUT_WIDTH = 0.55;

/**
 * Height of each successive layer of struts, seen down through the holes.
 *
 * A cut face of open-cell foam is not a field of dimples — it is a net, and
 * where the net is open you look through it onto the next net, and the one
 * after that, each dimmer and deeper. Stacking three offset lattices is what
 * turns the pattern from a mosaic into something with a hole in it, and it is
 * what the ambient occlusion map then has something to darken.
 */
const FOAM_LAYER_DEPTHS = [1, 0.5, 0.22];

/**
 * Below this much remaining visibility a deeper layer stops being evaluated.
 *
 * Each layer costs nine distance tests per texel, and where the layers above
 * have already closed over, whatever is underneath contributes less than the
 * 8-bit maps can encode.
 */
const FOAM_LAYER_CUTOFF = 0.05;

/**
 * How dark the deepest void goes, as a fraction taken off the ambient term.
 *
 * Deeper than the 0.55 this used to be, because the field it is applied to has
 * changed meaning: the old one was smooth clumping whose "low" points were the
 * shallow side of a blob, and this one has actual holes. There is nothing at
 * the bottom of a hole in a foam to light it — the light that enters is
 * absorbed against the struts on the way in and on the way out.
 *
 * It is also the strongest lever available on this material. The product scenes
 * have no analytic lights at all ({@link ProductViewerStage}), so every bit of
 * light here is indirect, which is exactly what `aoMap` attenuates. The
 * roughness map primarily changes the dielectric specular response. Three's
 * IBL multiscattering also couples roughness to diffuse energy, so the map
 * still matters even at `metalness: 0`.
 *
 * Mean ambient goes from 0.79 to 0.62, so the pads read about a fifth darker
 * overall, which is the point.
 */
const FOAM_AO_STRENGTH = 0.75;

/**
 * The pore maps, baked once for the whole session.
 *
 * Every foam style is cut from the same foam at the same {@link TEXTURE_SIZE},
 * so there is exactly one set — this used to be a keyed map with a sixteen
 * entry FIFO and a disposal path, none of which could ever run, and whose
 * eviction would in any case have disposed textures still bound to a mounted
 * pad now that they are handed out shared rather than cloned.
 */
let foamTextures: FoamTextures | undefined;

/** Solid fraction of the strut network at one point of one layer, in [0, 1]. */
function strutCoverage(lattice: CellularLattice, u: number, v: number): number {
  const edge = Math.min(
    1,
    cellularEdgeDistance(lattice, u, v) / FOAM_STRUT_WIDTH,
  );
  return 1 - edge * edge * (3 - 2 * edge);
}

/**
 * Open-cell foam: a network of ligaments with the deeper networks showing
 * through its holes.
 *
 * The cell walls sit on a Voronoi tessellation, which is the standard way to
 * generate the topology of a real foam — the cells nucleate together, grow
 * isotropically and stop where they meet a neighbour. What this replaced was a
 * sum of value-noise octaves, which has no cell structure at any amplitude and
 * whose normal map, measured, tilted the surface by 1.7 degrees on average.
 * This one tilts it by 20.
 */
function generateFoamHeightMap(size: number): Float32Array {
  const data = new Float32Array(size * size);
  const layers = FOAM_LAYER_DEPTHS.map((depth, index) => ({
    depth,
    // A salt per layer, or all three lattices would be the same net and the
    // holes would look straight down onto their own copies.
    lattice: cellularLattice(FOAM_CELLS_PER_TILE, index * 9176),
  }));

  for (let y = 0; y < size; y++) {
    const v = (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      const u = (x + 0.5) / size;

      let height = 0;
      // How much of this texel still sees past everything above it.
      let visible = 1;
      for (const { depth, lattice } of layers) {
        if (visible < FOAM_LAYER_CUTOFF) break;
        const coverage = strutCoverage(lattice, u, v);
        height = Math.max(height, depth * coverage * visible);
        visible *= 1 - coverage;
      }

      data[y * size + x] = height;
    }
  }

  return data;
}

function getFoamTextures(): FoamTextures {
  if (!foamTextures) {
    // Keep the expensive bake lazy, not its temporary 1 MiB Float32 field.
    // No macro relief belongs in this field: nubs and pores have different
    // physical scales and are sampled independently.
    const field = generateFoamHeightMap(TEXTURE_SIZE);
    foamTextures = {
      normalMap: heightMapToNormalMap(
        TEXTURE_SIZE,
        field,
        FOAM_NORMAL_INTENSITY,
      ),
      poreMap: heightMapToAoRoughnessReliefMap(
        TEXTURE_SIZE,
        field,
        FOAM_AO_STRENGTH,
        PORE_ROUGHNESS,
        SKIN_ROUGHNESS,
      ),
    };
  }
  return foamTextures;
}

/**
 * Edge length of the low-frequency tint map on the part, in millimetres.
 *
 * At any ordinary viewing distance the pores are below a pixel and the maps
 * above average out to a flat normal and a constant darkening — which is
 * correct, and is also why a pad read as a slab of grey plastic. What a cut
 * foam face shows at that distance is unevenness at the scale of centimetres:
 * density varies through the block, and the saw or hot wire leaves marks. A
 * few percent of brightness at this scale is what breaks the uniformity.
 */
const FOAM_TINT_TILE_MM = 160;
const FOAM_TINT_SIZE = 128;
const FOAM_TINT_CELLS = 6;
const FOAM_TINT_OCTAVES = 4;
/**
 * Linear-light multipliers decoded from a normalized density map. The old
 * 0.955–1.025 ramp was stored directly in sRGB bytes: 47% of texels clipped
 * to white, leaving only twelve levels. Decode after filtering instead, so
 * both sides of unity survive without a floating-point texture or extra tap.
 *
 * This range keeps the measured tile mean at 0.98660 versus the old decoded
 * 0.98657; fixing the encoding should not simply brighten the whole pad. The
 * variation stays subtle, especially on the light-blue foam.
 */
const FOAM_TINT_LOW = 0.935;
const FOAM_TINT_HIGH = 1.02;

/**
 * Shared soft-foam finish, in three's cloth terms.
 *
 * All padding styles use this broad grazing response, including the slotted
 * sheet and the painted cutout/thin-sheet fallbacks. Colour, macro shape and
 * geometric occlusion remain style-specific; the surface finish does not.
 * Sheen is a visual approximation of the cut cells' grazing response, not a
 * claim that all catalogue foams have identical physical microstructure.
 *
 * In three the sheen is lit from the environment's irradiance, which the
 * material already reads for the diffuse term, so this is arithmetic rather
 * than another environment lookup.
 */
const FOAM_SHEEN = 0.45;
const FOAM_SHEEN_ROUGHNESS = 0.95;

/**
 * Retain most of the modelled eggbox's horizon shading, without counting its
 * cavities at full strength twice under the viewer's 50 mm screen-space AO.
 * Painted nubs cannot enter that depth buffer, so their macro AO stays full.
 * This is a material weight: the geometry's physical horizon values, open
 * tips, side faces and the slotted sheet's shading remain unchanged.
 */
const SPIKED_BAKED_OCCLUSION_STRENGTH = 0.65;

/** How far the sheen colour sits from the foam colour towards white. */
const FOAM_SHEEN_LIFT = 0.5;

/**
 * Edge length of the eggbox macro maps on the part, in millimetres.
 *
 * Two nubs to a tile, on the same {@link SPIKE_PITCH_MM} lattice the spiked
 * geometry stands its nubs on, so a pad with cutouts wearing the painted
 * version agrees in scale with the pad next to it that has the real thing.
 * The previous eggbox blend, sharing the pore tile, put its bumps 10 mm apart.
 *
 * The macro is a 25 mm feature, so 128 texels at 0.4 mm each is plenty; the
 * bake is two cosines and one horizon test per texel.
 */
const FOAM_EGGBOX_TILE_MM = SPIKE_PITCH_MM * 2;

/**
 * Edge length of the groove macro maps, in millimetres: one groove to a tile,
 * on the pitch `createSlottedPadGeometry` cuts them at.
 */
const FOAM_SLOT_TILE_MM = SLOT_PITCH_MM;

const FOAM_MACRO_SIZE = 128;

/**
 * A macro relief for the pads whose geometry cannot carry it: the exact
 * normal of the shape, and the occlusion inside it. Both tile at
 * `tileMm` on the part.
 */
type FoamMacroTextures = {
  normalMap: THREE.CanvasTexture;
  aoMap: THREE.CanvasTexture;
  tileMm: number;
  /** Factor on the slope the normal map was baked at; see {@link FOAM_MACRO_NORMAL}. */
  slopeScale: number;
};

/**
 * Steps the painted nub height is rounded to, in mm.
 *
 * The occlusion between the nubs is not linear in their height — it comes out
 * of a horizon search — so unlike the normal map it has to be baked per
 * height. Quantizing to 2.5 mm reduces duplicate bakes but is not a bound on
 * cache growth when arbitrary custom heights are used. Measured against an
 * exact bake, this step moves occlusion by at most 0.02, against the 0.21 that
 * painting the catalogue's nub on a 50 mm pad moved it.
 */
const EGGBOX_NUB_STEP_MM = 2.5;

/**
 * The nub height the painted eggbox is baked at for a pad this thick.
 *
 * {@link spikedPadNubHeight} rounded to {@link EGGBOX_NUB_STEP_MM}; zero or
 * less means the pad is thinner than its own base plate and has no nub to
 * paint. Exported so the agreement between a painted pad and the modelled one
 * beside it can be asserted without a canvas to bake into.
 */
export function eggboxNubHeightMm(padThicknessMm: number): number {
  const nub = spikedPadNubHeight(padThicknessMm);
  if (nub <= 0) return 0;
  return Math.round(nub / EGGBOX_NUB_STEP_MM) * EGGBOX_NUB_STEP_MM;
}

let foamTintMap: THREE.CanvasTexture | undefined;
let foamEggboxNormalMap: THREE.CanvasTexture | undefined;
let foamSlotMacroTextures: FoamMacroTextures | undefined;

function getFoamTintMap(): THREE.CanvasTexture {
  if (foamTintMap) return foamTintMap;

  const size = FOAM_TINT_SIZE;
  const field = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      field[y * size + x] = tileableFbm(
        x / size,
        y / size,
        FOAM_TINT_CELLS,
        FOAM_TINT_OCTAVES,
      );
    }
  }
  // Density is data, not sRGB colour: use the full [0, 1] byte range. One
  // lazy 128² map supplies the same finish to every foam style.
  foamTintMap = heightMapToReliefMap(size, field);
  return foamTintMap;
}

/**
 * The nub slopes, baked once at {@link SPIKE_NUB_MM} for every height.
 *
 * The map holds a slope, and the slope of a nub is its height times the shape
 * of the profile, so one bake scaled by a ratio is the exact normal of a nub
 * of any height — see {@link FOAM_MACRO_NORMAL}. It is baked at the
 * catalogue's height rather than at unit height for the encoding's sake: the
 * profile's own slopes are around 0.1, which is a dozen of the 256 codes an
 * 8-bit normal map has and bands visibly, while 20 mm over a 25 mm pitch
 * spreads them across the range.
 */
function getFoamEggboxNormalMap(): THREE.CanvasTexture {
  if (!foamEggboxNormalMap) {
    const tile = FOAM_EGGBOX_TILE_MM;
    const pitch = SPIKE_PITCH_MM;
    foamEggboxNormalMap = slopeToNormalMap(FOAM_MACRO_SIZE, (u, v) => {
      const [hx, hz] = noppenSlope(u * tile, v * tile, pitch, pitch);
      return [SPIKE_NUB_MM * hx, SPIKE_NUB_MM * hz];
    });
  }
  return foamEggboxNormalMap;
}

/**
 * The eggbox as maps, from the same surface function the geometry is built
 * from: the exact normal, and the occlusion between the nubs.
 *
 * @param nubMm Nub height to paint, from {@link eggboxNubHeightMm}. The pads
 *   that wear these maps stand next to pads that carry the real nubs, and
 *   `createSpikedPadGeometry` gets that height from the pad's thickness — so
 *   the maps have to be built per height too, and not from the catalogue
 *   sheet's 20 mm, which is only right for a 30 mm pad.
 */
function getFoamMacroTextures(nubMm: number): FoamMacroTextures {
  const size = FOAM_MACRO_SIZE;
  const tile = FOAM_EGGBOX_TILE_MM;
  const pitch = SPIKE_PITCH_MM;
  const period = size / 2;

  const shade = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    // flipY: the top row of the canvas is v = 1.
    const v = 1 - (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      // The tile contains two periods on each axis. Reuse their exact baked
      // bytes, not a coarser map: resolution, texel centres and mips stay.
      if (x >= period || y >= period) {
        shade[y * size + x] = shade[
          (y % period) * size + (x % period)
        ] as number;
        continue;
      }
      const u = (x + 0.5) / size;
      shade[y * size + x] = noppenShade(
        u * tile,
        v * tile,
        pitch,
        pitch,
        nubMm,
      );
    }
  }

  // The per-height material cache already owns this bake; a second cache
  // would retain exactly the same textures without avoiding any extra work.
  return {
    normalMap: getFoamEggboxNormalMap(),
    aoMap: valuesToGrayMap(size, shade),
    tileMm: tile,
    slopeScale: nubMm / SPIKE_NUB_MM,
  };
}

/**
 * The grooves as maps.
 *
 * A normal map cannot hold a vertical wall, and a groove is nothing but two of
 * them, so the normal map here is flat and the whole of the groove is in the
 * occlusion: a stripe as wide as the groove and as dark as its floor. Seen
 * from anywhere but straight above that is what a 4 mm slit looks like anyway,
 * and from straight above it is all that can be seen of it.
 */
function getFoamSlotMacroTextures(): FoamMacroTextures {
  if (!foamSlotMacroTextures) {
    const size = FOAM_MACRO_SIZE;
    const tile = FOAM_SLOT_TILE_MM;
    const groove = slotShade(slotFloorVisibility(SLOT_DEPTH_MM, SLOT_WIDTH_MM));
    const half = SLOT_WIDTH_MM / 2 / tile;
    const texel = 1 / size;

    // One texel: the field is constant, so a 128² bake of it would be 16k
    // evaluations and a mip chain to encode `vec3(0, 0, 1)`.
    const normalMap = slopeToNormalMap(1, () => [0, 0]);

    const shade = new Float32Array(size * size);
    for (let y = 0; y < size; y++) {
      // The groove runs along u and sits at the centre of the tile in v.
      const v = 1 - (y + 0.5) / size;
      const inside = half - Math.abs(v - 0.5);
      // One texel of softening on the lip, so the stripe does not alias.
      const coverage = Math.min(1, Math.max(0, inside / texel + 0.5));
      const value = 1 - coverage * (1 - groove);
      for (let x = 0; x < size; x++) {
        shade[y * size + x] = value;
      }
    }

    foamSlotMacroTextures = {
      normalMap,
      aoMap: valuesToGrayMap(size, shade),
      tileMm: tile,
      // The map is flat, so there is no slope to scale.
      slopeScale: 1,
    };
  }
  return foamSlotMacroTextures;
}

/**
 * Object-space planar projection, down the dominant axis of the face normal.
 *
 * A pad is not a surface with a pattern printed on it — it is a piece sawn out
 * of a block of foam, and every face of it is a cut that opens the cells it
 * passes through. So the pores have to be the same physical size on all six
 * faces, and each face has to show its own slice rather than a copy of the same
 * one stretched to fit.
 *
 * The UVs on the geometry cannot express that. A box gives every face the same
 * 0..1 square regardless of how big the face is, so scaling the pattern with a
 * `repeat` taken from the pad's width and depth got the top face right and
 * squashed the sides by the ratio of the pad's depth to its thickness — a
 * factor of ten on a 30 mm pad. Projecting in millimetres removes the choice:
 * there is one scale and the geometry cannot disagree with it.
 *
 * Written into the map varyings from the vertex shader, so the fragment shader
 * costs exactly what it did — one tap per map, not the three a blended
 * triplanar would take — and three's derivative-based tangent frame picks the
 * projection up on its own.
 *
 * The axis is chosen per vertex, so a triangle whose corners disagree smears
 * between two projections across its span. On a box that never happens; on
 * the rounding of a block edge it happens within a millimetre; on the flank of
 * a nub it happened across the whole nub, which is why the spiked geometry
 * bakes its projection instead — see {@link FOAM_BAKED_PROJECTION}.
 */
const FOAM_SOLID_PROJECTION = /* glsl */ `
  vec3 foamAxis = abs( normal );
  bool foamTop = foamAxis.y >= foamAxis.x && foamAxis.y >= foamAxis.z;
  bool foamSide = ! foamTop && foamAxis.x >= foamAxis.z;
  vec2 foamMm = foamTop ? position.xz : ( foamSide ? position.zy : position.xy );
  vec2 foamCap = vec2( foamTop ? 1.0 : 0.0, ( foamTop || foamSide ) ? 0.0 : 1.0 );
`;

/**
 * The projection read off the geometry instead, in millimetres, from the `uv`
 * attribute `attachFoamAttributes` writes; with it the occlusion the geometry
 * baked per vertex. Zero decisions in the shader.
 */
const FOAM_BAKED_PROJECTION = /* glsl */ `
  vec2 foamMm = uv;
  vec2 foamCap = vec2( 0.0, 1.0 );
  vFoamOcclusion = ${FOAM_OCCLUSION_ATTRIBUTE};
`;

const FOAM_BAKED_VERTEX_PARS = /* glsl */ `
  attribute float ${FOAM_OCCLUSION_ATTRIBUTE};
  varying float vFoamOcclusion;
`;

const FOAM_BAKED_FRAGMENT_PARS = /* glsl */ `
  uniform float foamBakedOcclusionStrength;
  varying float vFoamOcclusion;
`;

/** Applied to the ambient term after the pore occlusion map. */
const FOAM_BAKED_OCCLUSION = /* glsl */ `
  ambientOcclusion *= mix( 1.0, vFoamOcclusion, foamBakedOcclusionStrength );
`;

const FOAM_TINT_FRAGMENT_PARS = /* glsl */ `
  uniform vec2 foamTintRange;
`;

/** Reuse the diffuse-map tap; alpha is not density and stays untouched. */
const FOAM_TINT_REMAP = /* glsl */ `
  sampledDiffuseColor.rgb = mix(
    vec3( foamTintRange.x ), vec3( foamTintRange.y ), sampledDiffuseColor.rgb
  );
`;

/**
 * The map varyings, from whichever projection was chosen.
 *
 * The tint map runs on its own, much larger tile. `foamCap` identifies the
 * y/z cap conventions: a thin block's main faces are on y, an extrusion's on z.
 */
const FOAM_MAP_VARYINGS = /* glsl */ `
  vec2 foamUv = foamMm / ${FOAM_TILE_MM.toFixed(1)};
  #ifdef USE_NORMALMAP
    vNormalMapUv = foamUv;
  #endif
  #ifdef USE_ROUGHNESSMAP
    vRoughnessMapUv = foamUv;
  #endif
  #ifdef USE_AOMAP
    vAoMapUv = foamUv;
  #endif
  #ifdef USE_MAP
    vMapUv = foamMm / ${FOAM_TINT_TILE_MM.toFixed(1)};
  #endif
`;

const FOAM_MACRO_VERTEX_PARS = /* glsl */ `
  uniform vec2 foamMacroCapWeights;
  varying vec2 vFoamMacroUv;
  varying float vFoamCap;
`;

const foamMacroVaryings = (tileMm: number) => /* glsl */ `
  vFoamMacroUv = foamMm / ${tileMm.toFixed(1)};
  vFoamCap = dot( foamCap, foamMacroCapWeights );
`;

const FOAM_MACRO_FRAGMENT_PARS = /* glsl */ `
  uniform sampler2D foamMacroNormalMap;
  uniform sampler2D foamMacroAoMap;
  uniform float foamMacroSlope;
  varying vec2 vFoamMacroUv;
  varying float vFoamCap;
`;

/**
 * Adds the macro's slope to the pore normal, on the cap only.
 *
 * Both maps are height fields over the same coordinate, and the slope of a sum
 * of height fields is the sum of their slopes: `xy / z` of a decoded normal is
 * its slope, so the macro's slope is added to the pore's in the pore's own
 * scale, and the normalisation that follows makes a unit normal of the sum.
 * That is exact, where the usual "add the xy and keep the z" blend is not, and
 * it has to be, because the macro is baked from a 58° flank and blending it
 * approximately would flatten it.
 *
 * `foamMacroSlope` rescales that slope, and being a slope is what lets it: the
 * nubs are one shape at one pitch, and their height only multiplies it, so the
 * eggbox's normals at every thickness come out of the single bake at
 * {@link SPIKE_NUB_MM} exactly rather than approximately. Only the occlusion
 * has to be baked per height.
 */
const FOAM_MACRO_NORMAL = /* glsl */ `
  vec3 foamMacroN = texture2D( foamMacroNormalMap, vFoamMacroUv ).xyz * 2.0 - 1.0;
  mapN.xy += foamMacroN.xy / max( foamMacroN.z, 0.1 )
    * foamMacroSlope * mapN.z * vFoamCap;
`;

const FOAM_MACRO_OCCLUSION = /* glsl */ `
  ambientOcclusion *= mix( 1.0, texture2D( foamMacroAoMap, vFoamMacroUv ).r, vFoamCap );
`;

/**
 * Depth of the pore relief, in millimetres — the distance the parallax march
 * below carries a view ray between the top of a strut and the bottom of the
 * height field.
 *
 * The field opens three cell layers deep, so this is a little over one pore
 * across. Taking it deeper does not buy more depth: the march can only find
 * what the height map records, and past a pore or two the map has run out of
 * layers, so the extra range just stretches the same relief into a smear.
 */
const FOAM_RELIEF_MM = 0.3;

/**
 * March steps the view ray takes into the foam, at most, plus its initial
 * relief sample (17 relief fetches at the maximum).
 *
 * A step is one texture fetch, and the count is chosen per fragment from how
 * far the ray sweeps across the screen — one step per pixel — so a pad seen
 * from across the case, where the whole relief is worth less than a pixel,
 * marches not at all and pays for a single comparison.
 */
const FOAM_PARALLAX_STEPS = 16;

/**
 * Parallax occlusion mapping over the pore height field.
 *
 * A normal map lights a flat face as though it were not flat, which holds until
 * the camera moves: the pores stay exactly where they are, so the surface reads
 * as a printed pattern rather than as holes. This walks the view ray into the
 * height field and shades the point where it first meets a strut, so the near
 * wall of a pore covers its far wall as the case turns — which is the whole of
 * what makes an open-cell surface look open.
 *
 * The march is bounded by how much it can be seen. The step count comes from
 * the length of the sweep in pixels, so the cost falls to nothing at the
 * distance a pad is usually viewed from, and only a close-up pays for sixteen
 * fetches. At 0.2 mm pores that is honest rather than thrifty: the effect is a
 * close-up effect, because the relief is a fraction of a pixel until then.
 *
 * `foamRay.z` is held away from zero. It is the cosine of the view angle, and
 * the offset it divides is unbounded at grazing incidence — where the geometry
 * of a flat height field stops being a model of anything anyway, since a real
 * strut would have hidden the pore long before.
 */
const FOAM_PARALLAX = /* glsl */ `
  vec2 foamUv = vNormalMapUv;

  #if ! defined( USE_TANGENT ) && defined( USE_NORMALMAP_TANGENTSPACE ) && ! defined( FLAT_SHADED )

    mat3 foamFrame = getTangentFrame( - vViewPosition, normalize( vNormal ), vNormalMapUv );
    vec3 foamEye = normalize( vViewPosition );
    vec3 foamRay = vec3(
      dot( foamEye, foamFrame[ 0 ] ),
      dot( foamEye, foamFrame[ 1 ] ),
      dot( foamEye, foamFrame[ 2 ] )
    );

    // Where the deepest sample of the field sits, seen from here.
    vec2 foamSweep = foamRay.xy / max( foamRay.z, 0.35 ) * foamRelief;

    // Taken once, outside the loop: inside it the coordinate is no longer
    // continuous, and a derivative of it would pick a mip level per pore.
    vec2 foamDdx = dFdx( vNormalMapUv );
    vec2 foamDdy = dFdy( vNormalMapUv );

    int foamSteps = int( clamp(
      length( foamSweep ) / max( max( length( foamDdx ), length( foamDdy ) ), 1e-8 ),
      0.0,
      float( ${FOAM_PARALLAX_STEPS} )
    ) );

    if ( foamSteps > 0 ) {

      float foamStride = 1.0 / float( foamSteps );
      vec2 foamOffset = foamSweep * foamStride;

      float foamDepth = 0.0;
      float foamSurface = 1.0 - textureGrad( foamReliefMap, foamUv, foamDdx, foamDdy ).b;
      float foamLastDepth = 0.0;
      float foamLastSurface = foamSurface;

      for ( int i = 0; i < foamSteps; i ++ ) {

        if ( foamDepth >= foamSurface ) break;

        foamLastDepth = foamDepth;
        foamLastSurface = foamSurface;
        foamDepth += foamStride;
        foamUv -= foamOffset;
        foamSurface = 1.0 - textureGrad( foamReliefMap, foamUv, foamDdx, foamDdy ).b;

      }

      // Between the last step outside the foam and the first one inside it,
      // where the ray and the height field actually cross.
      float foamInside = foamSurface - foamDepth;
      float foamOutside = foamLastSurface - foamLastDepth;
      foamUv += foamOffset * ( foamInside / min( foamInside - foamOutside, - 1e-5 ) );

    }

  #endif

  // AO and roughness use one final sample from the packed pore map. Relief
  // occupies B; the march above keeps the same gradients and physical depth.
  vec4 foamSurfaceSample = texture2D( foamReliefMap, foamUv );
`;

/**
 * Declarations for {@link FOAM_PARALLAX}, which go ahead of `main`.
 */
const FOAM_PARALLAX_PARS = /* glsl */ `
  uniform sampler2D foamReliefMap;
  uniform float foamRelief;
`;

/**
 * Pore lookups that follow the parallax offset. AO and roughness share a tap.
 *
 * Only the lookups. The tangent frame in `normal_fragment_begin` keeps the
 * unshifted coordinate: it is built from screen-space derivatives, and the
 * shifted one jumps from pore to pore, which would leave the frame — and with
 * it every normal on the surface — full of noise.
 *
 * The tint map is not among them: it is a few percent of brightness over
 * centimetres, and a third of a millimetre of shift is nothing to it.
 */
const FOAM_PARALLAX_LOOKUPS = [
  [
    "roughnessmap_fragment",
    "texture2D( roughnessMap, vRoughnessMapUv )",
    "foamSurfaceSample",
  ],
  ["aomap_fragment", "texture2D( aoMap, vAoMapUv )", "foamSurfaceSample"],
  ["normal_fragment_maps", ", vNormalMapUv )", ", foamUv )"],
] as const;

/** Where in the expanded chunks the variants add their own terms. */
const FOAM_TINT_HOOK = "diffuseColor *= sampledDiffuseColor;";
const FOAM_NORMAL_HOOK = "mapN.xy *= normalScale;";
const FOAM_OCCLUSION_HOOK = "aoMapIntensity + 1.0;";

type FoamShaderVariant = {
  /** Read the projection and occlusion the geometry baked; see `attachFoamAttributes`. */
  baked: boolean;
  /** Overlay macro maps on the cap, tiled at this many millimetres. */
  macroTileMm?: number;
};

/**
 * `String.replace`, but a needle that is not there is reported.
 *
 * Every splice below anchors on a literal fragment of a three shader chunk. A
 * plain `replace` that misses returns the input unchanged, so a three release
 * that reflows one of those chunks would quietly drop the baked occlusion, the
 * macro nubs or the parallax redirect — and the symptom, a foam pad that looks
 * like grey plastic, is exactly the one this material exists to avoid, so it
 * would read as an art regression rather than as a broken patch. A warning
 * rather than a throw: this runs inside `onBeforeCompile`, i.e. inside
 * `WebGLRenderer.render`, where throwing takes the whole canvas down.
 */
function spliceShader(
  source: string,
  needle: string,
  replacement: string,
  label: string,
): string {
  if (!source.includes(needle)) {
    console.warn(`Foam shader patch missed its anchor: ${label}`);
    return source;
  }
  return replaceVerbatim(source, needle, replacement);
}

function withFoamFragment(
  fragmentShader: string,
  variant: FoamShaderVariant,
): string {
  const tintChunk = spliceShader(
    THREE.ShaderChunk.map_fragment,
    FOAM_TINT_HOOK,
    `${FOAM_TINT_REMAP}\n${FOAM_TINT_HOOK}`,
    "tint multiplier",
  );
  let patched = spliceShader(
    fragmentShader,
    "#include <map_fragment>",
    tintChunk,
    "#include <map_fragment>",
  );

  // The includes are still unresolved at this point, so each one is swapped for
  // its own source with the lookup redirected.
  for (const [chunk, needle, replacement] of FOAM_PARALLAX_LOOKUPS) {
    const source = THREE.ShaderChunk[chunk];
    if (source === undefined) {
      console.warn(`Foam shader patch found no chunk named ${chunk}`);
      continue;
    }
    if (!source.includes(needle)) {
      console.warn(
        `Foam shader patch missed its anchor: ${needle} in ${chunk}`,
      );
    }
    patched = spliceShader(
      patched,
      `#include <${chunk}>`,
      replaceAllVerbatim(source, needle, replacement),
      `#include <${chunk}>`,
    );
  }

  if (variant.macroTileMm !== undefined) {
    patched = spliceShader(
      patched,
      FOAM_NORMAL_HOOK,
      `${FOAM_NORMAL_HOOK}\n${FOAM_MACRO_NORMAL}`,
      "macro normal",
    );
    patched = spliceShader(
      patched,
      FOAM_OCCLUSION_HOOK,
      `${FOAM_OCCLUSION_HOOK}\n${FOAM_MACRO_OCCLUSION}`,
      "macro occlusion",
    );
  }
  if (variant.baked) {
    patched = spliceShader(
      patched,
      FOAM_OCCLUSION_HOOK,
      `${FOAM_OCCLUSION_HOOK}\n${FOAM_BAKED_OCCLUSION}`,
      "baked occlusion",
    );
  }

  const pars = [
    FOAM_PARALLAX_PARS,
    FOAM_TINT_FRAGMENT_PARS,
    variant.baked ? FOAM_BAKED_FRAGMENT_PARS : "",
    variant.macroTileMm === undefined ? "" : FOAM_MACRO_FRAGMENT_PARS,
  ].join("\n");

  const main = "void main() {";
  return spliceShader(
    patched,
    main,
    `${pars}\n${main}\n${FOAM_PARALLAX}`,
    "fragment main",
  );
}

function withFoamVertex(
  vertexShader: string,
  variant: FoamShaderVariant,
): string {
  const pars = [
    variant.baked ? FOAM_BAKED_VERTEX_PARS : "",
    variant.macroTileMm === undefined ? "" : FOAM_MACRO_VERTEX_PARS,
  ].join("\n");
  const projection = [
    variant.baked ? FOAM_BAKED_PROJECTION : FOAM_SOLID_PROJECTION,
    FOAM_MAP_VARYINGS,
    variant.macroTileMm === undefined
      ? ""
      : foamMacroVaryings(variant.macroTileMm),
  ].join("\n");

  const main = "void main() {";
  const withPars = spliceShader(
    vertexShader,
    main,
    `${pars}\n${main}`,
    "vertex main",
  );
  return spliceShader(
    withPars,
    "#include <uv_vertex>",
    `#include <uv_vertex>\n${projection}`,
    "#include <uv_vertex>",
  );
}

type FoamMaterialOptions = {
  color: string;
  /** Baked projection and occlusion from the geometry, or projected here. */
  baked?: boolean;
  /** Macro maps to overlay on the cap; sets the shader variant's tile. */
  macro?: () => FoamMacroTextures;
  /** Extruded cutouts use z; thin rounded blocks use y. */
  macroCapAxis?: "y" | "z";
  /** Weight of geometric macro AO; pore AO and painted macros are independent. */
  bakedOcclusionStrength?: number;
};

/**
 * Keeps the patched program out of the cache slot of an identical unpatched
 * one, and the variants out of each other's.
 *
 * Nub height is deliberately not in it. The eggbox materials of two pad
 * thicknesses differ in a uniform and a texture, not in a line of source, so
 * they share one compiled program and three uploads each one's own uniforms —
 * which is the point of carrying the height as a uniform rather than baking it
 * into the shader.
 */
function foamProgramKey(variant: FoamShaderVariant): string {
  const macro =
    variant.macroTileMm === undefined
      ? "plain"
      : `macro-${variant.macroTileMm}`;
  return `foam-v4-${variant.baked ? "baked" : "projected"}-${macro}`;
}

function createFoamMaterial({
  color,
  baked = false,
  macro,
  macroCapAxis = "z",
  bakedOcclusionStrength = 1,
}: FoamMaterialOptions): THREE.MeshPhysicalMaterial {
  // The finish is shared, not a per-style option: only colour and the macro
  // geometry's projection/occlusion differ between the material factories.
  const textures = getFoamTextures();
  // Baked once here rather than in `onBeforeCompile`: the tile has to be known
  // for the program key before the shader is ever compiled.
  const macroTextures = macro?.();
  const variant: FoamShaderVariant = {
    baked,
    macroTileMm: macroTextures?.tileMm,
  };
  const base = new THREE.Color(color);
  const material = new THREE.MeshPhysicalMaterial({
    color: base,
    map: getFoamTintMap(),
    // The map carries absolute roughness; three multiplies the two together.
    roughness: 1,
    roughnessMap: textures.poreMap,
    metalness: 0,
    normalMap: textures.normalMap,
    normalScale: new THREE.Vector2(1, 1),
    aoMap: textures.poreMap,
    sheen: FOAM_SHEEN,
    sheenRoughness: FOAM_SHEEN_ROUGHNESS,
    sheenColor: base.clone().lerp(new THREE.Color(1, 1, 1), FOAM_SHEEN_LIFT),
  });

  material.onBeforeCompile = (shader) => {
    shader.uniforms.foamReliefMap = { value: textures.poreMap };
    // In UV units, because that is what the march offsets.
    shader.uniforms.foamRelief = { value: FOAM_RELIEF_MM / FOAM_TILE_MM };
    // Fresh uniform objects per material/compile, even where source is shared.
    shader.uniforms.foamTintRange = {
      value: new THREE.Vector2(FOAM_TINT_LOW, FOAM_TINT_HIGH),
    };
    if (baked) {
      shader.uniforms.foamBakedOcclusionStrength = {
        value: bakedOcclusionStrength,
      };
    }
    if (macroTextures) {
      shader.uniforms.foamMacroCapWeights = {
        value:
          macroCapAxis === "y"
            ? new THREE.Vector2(1, 0)
            : new THREE.Vector2(0, 1),
      };
      shader.uniforms.foamMacroNormalMap = { value: macroTextures.normalMap };
      shader.uniforms.foamMacroAoMap = { value: macroTextures.aoMap };
      shader.uniforms.foamMacroSlope = { value: macroTextures.slopeScale };
    }

    shader.vertexShader = withFoamVertex(shader.vertexShader, variant);
    shader.fragmentShader = withFoamFragment(shader.fragmentShader, variant);
  };
  material.customProgramCacheKey = () => foamProgramKey(variant);

  return material;
}

const foamMaterialCache = new Map<string, THREE.MeshPhysicalMaterial>();

/**
 * One material per style, for the whole scene.
 *
 * Nothing about a foam material depends on the pad wearing it any more, so
 * there is no reason for two pads to hold two of them — and every reason not
 * to, since a material per pad is a shader program lookup per pad and, as these
 * were never disposed, a leak on every style change.
 */
function sharedFoamMaterial(
  key: string,
  create: () => THREE.MeshPhysicalMaterial,
): THREE.MeshPhysicalMaterial {
  let material = foamMaterialCache.get(key);
  if (!material) {
    material = create();
    foamMaterialCache.set(key, material);
  }
  return material;
}

/** Standard/Default foam: dunkelgrau, feine zufällige Poren. */
export const getStandardFoamMaterial = (): THREE.MeshPhysicalMaterial =>
  sharedFoamMaterial("standard", () =>
    createFoamMaterial({
      color: "#333333",
    }),
  );

/** Weicher Foam: hellblau, gleiche Porenstruktur. */
export const getSoftFoamMaterial = (): THREE.MeshPhysicalMaterial =>
  sharedFoamMaterial("soft", () =>
    createFoamMaterial({
      color: "#aaccff",
    }),
  );

/**
 * Noppenschaum, dessen Noppen in der Geometrie stecken: nur noch Poren.
 *
 * Worn by the pads `createSpikedPadGeometry` builds, and reads the projection
 * and the occlusion that geometry baked onto its vertices.
 */
export const getSpikedFoamMaterial = (): THREE.MeshPhysicalMaterial =>
  sharedFoamMaterial("spiked", () =>
    createFoamMaterial({
      color: "#4a4a4a",
      baked: true,
      bakedOcclusionStrength: SPIKED_BAKED_OCCLUSION_STRENGTH,
    }),
  );

/**
 * Noppenschaum als Textur, für Polster mit Ausschnitten.
 *
 * The extruded cutout geometry has no tessellation in its cap to displace, so
 * those pads keep a painted-on version: the same pores as every other foam,
 * with the nubs overlaid from their own maps on the same 25 mm lattice as the
 * real ones. The silhouette stays flat; the shading and the shadow between
 * the nubs do not.
 *
 * @param padThicknessMm Thickness of the pad wearing it, since that is what
 *   sets how tall its nubs are. A material per height rather than one for the
 *   scene: a 50 mm pad's nubs are 40 mm, and painting the catalogue sheet's
 *   20 mm on it put a tenth of the ambient light back into valleys that the
 *   modelled pad beside it renders dark. Thicknesses come from the padding
 *   catalogue and are rounded on top of that, so this is a handful of entries.
 *   At or below the base plate's thickness there is no nub to paint and the
 *   pad gets the plain foam — which is what `createSpikedPadGeometry` falls
 *   back to modelling there anyway.
 */
export const getEggboxFoamMaterial = (
  padThicknessMm: number,
): THREE.MeshPhysicalMaterial => {
  const nubMm = eggboxNubHeightMm(padThicknessMm);
  if (nubMm <= 0) {
    return sharedFoamMaterial("eggbox-flat", () =>
      createFoamMaterial({
        color: "#4a4a4a",
      }),
    );
  }
  return sharedFoamMaterial(`eggbox-${nubMm}`, () =>
    createFoamMaterial({
      color: "#4a4a4a",
      macro: () => getFoamMacroTextures(nubMm),
    }),
  );
};

/**
 * Colour of the grooved sheet: black, per the datasheet.
 */
const SLOTTED_COLOR = "#2c2c2c";

/**
 * Geschlitzter Schaumstoff, dessen Nuten in der Geometrie stecken: nur noch
 * die Poren.
 *
 * Worn by the pads `createSlottedPadGeometry` builds, and reads the projection
 * and the occlusion that geometry baked onto its vertices. Shares its shader
 * program with the spiked material.
 *
 * Uses the same pores, density tint and broad sheen as the soft foam. Keep
 * the groove-specific occlusion: a narrow saw cut does not have the same
 * horizon visibility as an eggbox valley.
 */
export const getGroovedFoamMaterial = (): THREE.MeshPhysicalMaterial =>
  sharedFoamMaterial("grooved", () =>
    createFoamMaterial({
      color: SLOTTED_COLOR,
      baked: true,
    }),
  );

/**
 * Geschlitzter Schaumstoff als Textur, für Polster mit Ausschnitten.
 *
 * The same arrangement as the eggbox: the pores from the shared maps, the
 * grooves from their own, on the pitch the real ones are cut at.
 */
export const getSlottedFoamMaterial = (
  capAxis: "y" | "z" = "z",
): THREE.MeshPhysicalMaterial =>
  sharedFoamMaterial(capAxis === "y" ? "slotted-block" : "slotted", () =>
    createFoamMaterial({
      color: SLOTTED_COLOR,
      macro: getFoamSlotMacroTextures,
      macroCapAxis: capAxis,
    }),
  );
